import React, { useCallback, useEffect, useState } from 'react';

import {
    Image,
    ImageSourcePropType,
    StyleProp,
    View,
    ViewStyle,
} from 'react-native';

import FastImage, {
    Priority,
    ResizeMode,
    Source,
} from 'react-native-fast-image';
import Animated, {
    ReduceMotion,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { isWeb } from 'utils/responsiveness';

import NagraImage from 'components/NagraImage';
import { NagraImageUrlParams } from 'components/NagraImage/types';

import styles from './styles';

type Props = NagraImageUrlParams & {
    backupSource?: string;
    prio?: Priority;
    style?: StyleProp<ViewStyle>;
    resizeMode?: ResizeMode;
    cache?: Source['cache'];
    placeHolder?: null | ImageSourcePropType;
    backupSourceResizeMode?: ResizeMode;
};

const BaseTileImage = ({
    backupSource,
    prio = FastImage.priority.normal,
    style = {},
    resizeMode = FastImage.resizeMode.contain,
    cache = isWeb
        ? FastImage.cacheControl.web
        : FastImage.cacheControl.immutable,
    placeHolder = null,
    backupSourceResizeMode = FastImage.resizeMode.cover,
    contentId,
    contentType,
    imageType,
    width,
    height,
    aspect,
}: Props) => {
    const fadeAnim = useSharedValue(0);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageSource, setImageSource] = useState<string | undefined>(
        undefined,
    );
    const [resize, setResize] = useState(resizeMode);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            opacity: fadeAnim.value,
        };
    }, []);

    const handleImageLoad = useCallback(() => {
        fadeAnim.value = withTiming(
            1,
            { duration: 500, reduceMotion: ReduceMotion.Never },
            () => {
                runOnJS(setImageLoaded)(true);
            },
        );
    }, []);

    useEffect(() => {
        setImageSource(undefined);
        setImageLoaded(false);
    }, [contentId, placeHolder]);

    const handleImageError = useCallback(() => {
        setResize(backupSourceResizeMode);
        setImageSource(backupSource);

        fadeAnim.value = withTiming(
            0,
            { duration: 500, reduceMotion: ReduceMotion.Never },
            () => {
                runOnJS(setImageLoaded)(false);
            },
        );
    }, [backupSourceResizeMode, backupSource]);

    return (
        <View style={[style, styles.overFlow]}>
            {!imageLoaded && placeHolder && (
                <Image
                    source={placeHolder}
                    style={styles.container}
                    resizeMode="cover"
                />
            )}
            <Animated.View style={[styles.container, animatedStyle]}>
                <NagraImage
                    source={imageSource}
                    resizeMode={resize}
                    cache={cache}
                    style={styles.container}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    priority={prio}
                    contentType={contentType}
                    contentId={contentId}
                    imageType={imageType}
                    width={width}
                    height={height}
                    aspect={aspect}
                />
            </Animated.View>
        </View>
    );
};

export default BaseTileImage;
